import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useLiveQuery } from "dexie-react-hooks";
import { db } from "../../schema.dexie";
import { getCriancaAtiva } from "../../db/queries";
import { listarExperiencias, registrarMomentoCuidador } from "../queries";
import {
  CONTEXTOS_ORDEM,
  CONTEXTO_LABEL,
  type ContextoMomento,
  type EstadoRegulacao,
  type PassoEngajamento,
} from "../types";
import { EngajamentoSelector } from "../ui/EngajamentoSelector";
import { ExperienciaFoto } from "../ui/ExperienciaFoto";
import { StatusExperienciaBadge } from "../ui/StatusExperienciaBadge";
import { RegulacaoSelector } from "../ui/RegulacaoSelector";

/**
 * P0.2 — registro rápido (≤10s no caminho feliz): escolher experiência (recentes
 * no topo) → tocar o passo de engajamento → salvar. Regulação antes/depois e
 * contexto são OPCIONAIS e nunca bloqueiam.
 */
export function RegistrarMomento() {
  const { experienciaId } = useParams();
  const nav = useNavigate();
  const crianca = useLiveQuery(getCriancaAtiva);
  const experiencias = useLiveQuery(() => listarExperiencias()) ?? [];
  const momentos = useLiveQuery(() => db.momento.toArray()) ?? [];

  const [selecionado, setSelecionado] = useState<string | null>(experienciaId ?? null);
  const [passo, setPasso] = useState<PassoEngajamento | null>(null);
  const [antes, setAntes] = useState<EstadoRegulacao | null>(null);
  const [depois, setDepois] = useState<EstadoRegulacao | null>(null);
  const [contexto, setContexto] = useState<ContextoMomento | "">("");
  const [salvo, setSalvo] = useState(false);

  // recentes no topo: pela data do último momento; depois por criação.
  const ordenadas = useMemo(() => {
    const ultimo = new Map<string, string>();
    for (const m of momentos) {
      const at = ultimo.get(m.experiencia_id);
      if (!at || m.data_hora > at) ultimo.set(m.experiencia_id, m.data_hora);
    }
    return [...experiencias].sort((a, b) => {
      const ua = ultimo.get(a.id) ?? a.criado_em;
      const ub = ultimo.get(b.id) ?? b.criado_em;
      return ub.localeCompare(ua);
    });
  }, [experiencias, momentos]);

  const experiencia = experiencias.find((e) => e.id === selecionado) ?? null;

  async function salvar() {
    if (!experiencia || passo === null || !crianca) return;
    await registrarMomentoCuidador({
      experiencia_id: experiencia.id,
      crianca_id: crianca.id,
      passo_engajamento: passo,
      estado_antes: antes,
      estado_depois: depois,
      contexto: contexto || null,
    });
    setSalvo(true);
    setTimeout(() => nav("/compasso", { replace: true }), 900);
  }

  if (salvo) {
    return (
      <main className="tela">
        <div className="vazio">
          <span className="emoji" aria-hidden>
            🎵
          </span>
          <div style={{ fontSize: "1.3rem", color: "var(--accent-forte)" }}>salvo!</div>
        </div>
      </main>
    );
  }

  // passo 1 — escolher experiência
  if (!experiencia) {
    return (
      <main className="tela">
        <button className="btn btn-fantasma" onClick={() => nav(-1)}>
          ← voltar
        </button>
        <h1 className="tela-titulo">registrar momento</h1>
        <p className="tela-sub">qual experiência aconteceu?</p>
        {ordenadas.length === 0 ? (
          <div className="vazio">
            <span className="emoji" aria-hidden>
              🎵
            </span>
            cadastre uma experiência na biblioteca primeiro.
          </div>
        ) : (
          ordenadas.map((e) => (
            <button
              className="card linha-alimento"
              key={e.id}
              onClick={() => setSelecionado(e.id)}
            >
              <ExperienciaFoto midias={e.midias} nome={e.nome} tipo={e.tipo} />
              <div className="crescer">
                <div className="nome">{e.nome}</div>
              </div>
              <StatusExperienciaBadge status={e.status} />
            </button>
          ))
        )}
      </main>
    );
  }

  // passo 2 — escada de engajamento + regulação/contexto (opcionais)
  return (
    <main className="tela">
      <button
        className="btn btn-fantasma"
        onClick={() => (experienciaId ? nav(-1) : setSelecionado(null))}
      >
        ← trocar experiência
      </button>

      <div className="row" style={{ margin: "var(--s2) 0 var(--s4)" }}>
        <ExperienciaFoto midias={experiencia.midias} nome={experiencia.nome} tipo={experiencia.tipo} />
        <h1 className="tela-titulo" style={{ margin: 0 }}>
          {experiencia.nome}
        </h1>
      </div>

      <h2 className="secao-titulo">como foi? toque o passo de engajamento</h2>
      <EngajamentoSelector valor={passo} onChange={setPasso} />

      <section className="secao" style={{ marginTop: "var(--s5)" }}>
        <h2 className="secao-titulo">
          regulação <span style={{ color: "var(--text-suave)" }}>(opcional)</span>
        </h2>
        <div className="card">
          <div className="campo">
            <label>antes</label>
            <RegulacaoSelector valor={antes} onChange={setAntes} ariaLabel="estado antes" />
          </div>
          <div className="campo" style={{ margin: 0 }}>
            <label>depois</label>
            <RegulacaoSelector valor={depois} onChange={setDepois} ariaLabel="estado depois" />
          </div>
        </div>
      </section>

      <section className="secao">
        <h2 className="secao-titulo">
          contexto <span style={{ color: "var(--text-suave)" }}>(opcional)</span>
        </h2>
        <div className="card">
          <div className="campo" style={{ margin: 0 }}>
            <label>quando / onde</label>
            <select value={contexto} onChange={(e) => setContexto(e.target.value as ContextoMomento | "")}>
              <option value="">—</option>
              {CONTEXTOS_ORDEM.map((c) => (
                <option key={c} value={c}>
                  {CONTEXTO_LABEL[c]}
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      <button
        className="btn btn-primario btn-bloco btn-grande"
        disabled={passo === null}
        onClick={salvar}
      >
        salvar momento
      </button>
    </main>
  );
}
